/**
 * Catalog Service for ARGUS Olymp Platform
 *
 * Connects the Data Catalog UI to the olymp-backend REST API.
 */
import { API_ENDPOINTS, OLYMP_API_BASE_URL } from '../config/api';
// Der Sonderwert fuer "kein Archetyp zugeordnet" (#788) wohnt in
// `utils/helpers`, nicht hier: die Seitentests bilden diesen Dienst mit
// `jest.mock` nach und fuehren darin nur Funktionen -- eine Konstante von
// hier waere dort `undefined`. Weitergereicht wird sie trotzdem, damit
// Aufrufer des Dienstes sie nicht aus zwei Richtungen holen muessen.
import { ARCHETYP_OHNE_ZUORDNUNG } from '../pages/olymp/data_catalog/utils/helpers';

export { ARCHETYP_OHNE_ZUORDNUNG };

const LISTS_BASE = `${OLYMP_API_BASE_URL}${API_ENDPOINTS.LISTS.LIST}`.replace(/\/$/, '');
const SETS_BASE = `${OLYMP_API_BASE_URL}${API_ENDPOINTS.SETS.LIST}`.replace(/\/$/, '');
const REQUESTS_BASE = `${OLYMP_API_BASE_URL}${API_ENDPOINTS.DATA_REQUESTS.LIST}`.replace(/\/$/, '');
const FETCH_TIMEOUT_MS = 10000;
const MAX_PAGES = 50;

function buildCatalogUrl(endpoint) {
  return `${OLYMP_API_BASE_URL}${endpoint}`;
}

export function getParameterLookupId(parameter) {
  if (parameter === null || parameter === undefined) {
    return '';
  }

  if (typeof parameter !== 'object') {
    return String(parameter);
  }

  return String(parameter.parameterId || parameter.id || '');
}

function buildCatalogDetailUrl(id, path = '') {
  const detailUrl = buildCatalogUrl(API_ENDPOINTS.CATALOG.DETAIL.replace('{id}', id)).replace(/\/$/, '');
  const normalizedPath = String(path).replace(/^\/+|\/+$/g, '');
  if (!normalizedPath) {
    return `${detailUrl}/`;
  }
  return `${detailUrl}/${normalizedPath}/`.replace(/([^:]\/)\/+/g, '$1');
}

function buildListUrl(path = '') {
  const normalizedPath = String(path).replace(/^\/+|\/+$/g, '');
  const base = `${LISTS_BASE}/`;
  if (!normalizedPath) {
    return base;
  }
  return `${base}${normalizedPath}/`.replace(/([^:]\/)\/+/g, '$1');
}

function buildSetUrl(path = '') {
  const normalizedPath = String(path).replace(/^\/+|\/+$/g, '');
  const base = `${SETS_BASE}/`;
  if (!normalizedPath) {
    return base;
  }
  return `${base}${normalizedPath}/`.replace(/([^:]\/)\/+/g, '$1');
}

function buildRequestUrl(path = '') {
  const normalizedPath = String(path).replace(/^\/+|\/+$/g, '');
  const base = `${REQUESTS_BASE}/`;
  if (!normalizedPath) {
    return base;
  }
  return `${base}${normalizedPath}/`.replace(/([^:]\/)\/+/g, '$1');
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function getAuthHeaders() {
  const token = localStorage.getItem('authToken');
  const authHeaders = token
    ? { Authorization: `Bearer ${token}` }
    : {};

  return {
    'Content-Type': 'application/json',
    ...authHeaders,
  };
}

function buildRequestHeaders({ includeAuth = true } = {}) {
  if (!includeAuth) {
    return {
      'Content-Type': 'application/json',
    };
  }

  return getAuthHeaders();
}

async function apiFetch(url, options = {}) {
  async function doFetch(includeAuth = true) {
    return fetch(url, {
      method: options.method || 'GET',
      headers: buildRequestHeaders({ includeAuth }),
      body: options.body ? JSON.stringify(options.body) : undefined,
      signal: controller.signal,
    });
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  const response = await doFetch(true)
    .then(async initialResponse => {
      if (
        initialResponse.status === 401
        && options.allowAnonymousRetry
        && localStorage.getItem('authToken')
      ) {
        return doFetch(false);
      }

      return initialResponse;
    })
    .finally(() => {
      window.clearTimeout(timeoutId);
    });

  if (!response.ok) {
    const error = new Error(
      response.status === 401
        ? 'Authentication required. Please sign in again.'
        : `API error ${response.status}: ${url}`
    );
    error.status = response.status;
    error.url = url;
    throw error;
  }
  if (response.status === 204) {
    return null;
  }
  return response.json();
}

function parseOptionalNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeArray(value) {
  return Array.isArray(value) ? value : [];
}

/**
 * Einheiten, die sich nur in Gross-/Kleinschreibung unterscheiden (`mL`/`ml`),
 * sind eine Auspraegung, keine zwei (#264) -- dieselbe Fehlerklasse wie
 * `Blood`/`BLOOD` in `d_labitems` (#251). `unit: null` (nicht erfasste
 * Einheit) bleibt eigenstaendig; sie mit einer echten Einheit zusammenzulegen
 * wuerde genau den Unterschied verstecken, den #264 sichtbar machen soll.
 *
 * Angezeigt wird die Schreibweise mit dem groessten Einzelanteil (Fallback:
 * Anzahl), nicht die zuerst gesehene -- sonst wuerde eine seltene Schreibweise
 * (`mL` mit 5 %) die haeufige (`ml` mit 65 %) im Label verdraengen, nur weil
 * sie in der API-Antwort zuerst stand. `sources` werden dedupliziert: dieselbe
 * Quelle unter zwei Schreibweisen sollte nicht doppelt in der Liste stehen.
 *
 * Nach dem Zusammenlegen wird neu nach Anteil (Fallback: Anzahl) sortiert,
 * weil `index === 0` andernorts als "Standard-Einheit" gilt -- ohne Neusortierung
 * koennte eine zusammengelegte Einheit die haeufigste sein, ohne vorn zu stehen.
 */
function mergeUnitsByCasing(units) {
  const alle = normalizeArray(units);
  if (alle.length < 2) {
    return alle;
  }

  const summiere = (a, b) => {
    const an = parseOptionalNumber(a);
    const bn = parseOptionalNumber(b);
    if (an == null && bn == null) return null;
    return (an || 0) + (bn || 0);
  };

  const gewicht = (eintrag) => parseOptionalNumber(eintrag.percentage) ?? parseOptionalNumber(eintrag.count) ?? 0;

  const reihenfolge = [];
  const gruppen = new Map();

  alle.forEach((eintrag) => {
    const schluessel = typeof eintrag.unit === 'string' ? eintrag.unit.trim().toLowerCase() : eintrag.unit;
    const bestehend = gruppen.get(schluessel);
    if (!bestehend) {
      gruppen.set(schluessel, { ...eintrag, sources: Array.isArray(eintrag.sources) ? [...eintrag.sources] : eintrag.sources });
      reihenfolge.push(schluessel);
      return;
    }
    if (gewicht(eintrag) > gewicht(bestehend)) {
      bestehend.unit = eintrag.unit;
    }
    bestehend.count = summiere(bestehend.count, eintrag.count);
    bestehend.percentage = summiere(bestehend.percentage, eintrag.percentage);
    if (Array.isArray(eintrag.sources)) {
      bestehend.sources = [...new Set([...(bestehend.sources || []), ...eintrag.sources])];
    }
  });

  return reihenfolge
    .map((schluessel) => gruppen.get(schluessel))
    .sort((a, b) => gewicht(b) - gewicht(a));
}

function normalizeObject(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value;
}

function normalizeSynonym(synonym) {
  if (typeof synonym === 'string') {
    return {
      term: synonym,
      language: 'und',
    };
  }

  if (!synonym || typeof synonym !== 'object') {
    return null;
  }

  return {
    ...synonym,
    term: synonym.term || synonym.name || '',
    language: synonym.language || 'und',
  };
}

function normalizeComment(comment) {
  if (!comment || typeof comment !== 'object') {
    return null;
  }

  return {
    ...comment,
    role: comment.role || comment.authorRole || '',
    date: comment.date || comment.timestamp || '',
    replies: normalizeArray(comment.replies)
      .map(normalizeComment)
      .filter(Boolean),
  };
}

function normalizeStationCoverageEntry(station) {
  if (!station || typeof station !== 'object') {
    return null;
  }

  // `percentage` ist das einzige numerische Feld einer Station -- ohne
  // `parseOptionalNumber` liefe ein als String gelieferter Wert ("45") an
  // `hasNumber` in FrequencyMetrics.js vorbei und die Station zaehlte dort
  // als fehlend statt als 45 % (Rueckmeldung Runde 2, NEU B1).
  return {
    ...station,
    percentage: parseOptionalNumber(station.percentage),
  };
}

function normalizeFrequencyMetrics(apiItem, displayData) {
  // #54/#101: `apiItem.statistics` ist die vom Katalog-Import dokumentierte,
  // belegte Quelle (dieselbe, aus der `availableSince`/`usagePattern` weiter
  // unten in transformParameter schon lesen). `apiItem.frequencyMetrics`
  // bleibt als Ruecksicherung fuer Antworten, die die Kennzahlen noch flach
  // am Item mitliefern -- der tiefe `displayData.frequencyMetrics`-Pfad
  // entfaellt hier bewusst (Rueckmeldung Runde 1, AK2): keine noch so
  // konkurrierende Quelle greift mehr auf ihn zu.
  //
  // Aufloesung je Feld, nicht je Objekt (Rueckmeldung Runde 2, NEU B1): ein
  // `statistics`, das nur `availableSince`/`usagePattern` traegt (#53), ist
  // trotzdem "belegt" im Sinne von `normalizeObject` und haette als ganzes
  // Objekt jedes Feld aus einem daneben belegten `frequencyMetrics`
  // verdraengt. `statistik?.feld ?? bundle?.feld` laesst ein einzelnes Feld
  // nur dann leer, wenn es in beiden Quellen fehlt.
  const statistik = normalizeObject(apiItem.statistics);
  const bundle = normalizeObject(apiItem.frequencyMetrics);
  const stationCoverage = normalizeArray(apiItem.stationCoverage);
  const measurementPattern =
    normalizeObject(apiItem.measurementPattern) ||
    normalizeObject(statistik?.measurementPattern) ||
    normalizeObject(bundle?.measurementPattern);
  const legacyCaseCoverage = parseOptionalNumber(apiItem.caseCoverage);
  const legacyStationCount = parseOptionalNumber(apiItem.stationCount);

  // #266: der Zeitpunkt des Profilings, ISO-Rohformat -- dieselbe Quelle wie
  // `profiledAt` weiter unten (#243). Die Detailkarte formatiert das Datum
  // selbst, sprachabhaengig, statt einen beim Import eingefrorenen Satz zu zeigen.
  const extractedAt = apiItem.extractedAt || displayData?.extractedAt || statistik?.extractedAt || bundle?.extractedAt || null;
  // #102: `suppressed` kann als eigenes Feld direkt am API-Item stehen, ohne
  // dass sonst ein Frequenz-Bundle mitkommt (die Fallzahl liegt unter der
  // Schwelle, also gibt es keine echten Kennzahlen zu liefern). Ein solches
  // Bundle-loses Flag darf nicht am fruehen `return null` unten vorbeirauschen
  // -- sonst gated ExpandedRow.js seinen ganzen Frequenz-Block auf
  // `frequencyMetrics` und die Unterdrueckungs-Anzeige verschwindet still.
  const suppressed = Boolean(statistik?.suppressed ?? bundle?.suppressed ?? apiItem.suppressed);
  const suppressionThreshold = parseOptionalNumber(
    statistik?.suppressionThreshold ?? bundle?.suppressionThreshold ?? apiItem.suppressionThreshold
  );

  const result = {
    suppressed,
    suppressionThreshold,
    caseCoverage: parseOptionalNumber(statistik?.caseCoverage ?? bundle?.caseCoverage) ?? legacyCaseCoverage,
    stationCount: parseOptionalNumber(statistik?.stationCount ?? bundle?.stationCount) ?? legacyStationCount,
    totalCases: parseOptionalNumber(statistik?.totalCases ?? bundle?.totalCases),
    casesWithParameter: parseOptionalNumber(statistik?.casesWithParameter ?? bundle?.casesWithParameter),
    stationCoverage: (stationCoverage.length > 0
      ? stationCoverage
      : normalizeArray(statistik?.stationCoverage ?? bundle?.stationCoverage))
      .map(normalizeStationCoverageEntry)
      .filter(Boolean),
    avgMeasurementsPerCase: parseOptionalNumber(statistik?.avgMeasurementsPerCase ?? bundle?.avgMeasurementsPerCase),
    medianMeasurementsPerCase: parseOptionalNumber(statistik?.medianMeasurementsPerCase ?? bundle?.medianMeasurementsPerCase),
    totalMeasurements: parseOptionalNumber(statistik?.totalMeasurements ?? bundle?.totalMeasurements),
    continuousCases: parseOptionalNumber(statistik?.continuousCases ?? bundle?.continuousCases),
    singleMeasurementCases: parseOptionalNumber(statistik?.singleMeasurementCases ?? bundle?.singleMeasurementCases),
    measurementPattern,
    extractedAt,
  };

  // Rueckmeldung Runde 4, NEU B1: `!statistik && !bundle` fragte nur, ob das
  // Rohobjekt existiert -- ein `statistics`, das laut #53 nur
  // `availableSince`/`usagePattern` traegt (Felder, die transformParameter
  // weiter unten direkt aus `apiItem.statistics` liest, nicht aus dieser
  // Funktion), machte `statistik` bereits truthy und verhinderte den fruehen
  // `return null`, obwohl kein einziges Frequenzfeld belegt war. Die
  // zurueckgegebene Huelle aus lauter `null`s zaehlte dann in
  // `hasStructuredDetailData`/`hasDetailData` als "Detaildaten vorhanden" und
  // ParameterRow.js liess den Nachladevorgang aus -- die aufgeklappte Zeile
  // zeigte ueberall "keine Angabe", auch dort, wo der Nachladevorgang
  // Einheiten geliefert haette (AK4). Die Pruefung fragt jetzt die
  // tatsaechlich berechneten Felder, nicht die Rohquelle.
  const hasFrequencyData =
    result.suppressed ||
    result.caseCoverage !== null ||
    result.stationCount !== null ||
    result.totalCases !== null ||
    result.casesWithParameter !== null ||
    result.stationCoverage.length > 0 ||
    result.avgMeasurementsPerCase !== null ||
    result.medianMeasurementsPerCase !== null ||
    result.totalMeasurements !== null ||
    result.continuousCases !== null ||
    result.singleMeasurementCases !== null ||
    Boolean(result.measurementPattern);

  return hasFrequencyData ? result : null;
}

function normalizeWorkingListRequest(request) {
  if (!request || typeof request !== 'object') {
    return null;
  }

  return {
    parameterId: request.parameterId,
    requestedBy: request.requestedBy || '',
    createdAt: request.createdAt || null,
  };
}

function transformWorkingList(apiList) {
  if (!apiList || typeof apiList !== 'object') {
    return null;
  }

  const usageContexts = normalizeArray(apiList.usageContexts).map(context => String(context || '').trim()).filter(Boolean);
  const inferredListType =
    apiList.listType
    || (
      usageContexts.length === 1
        ? (usageContexts[0] === 'extraction' ? 'extraction' : 'criteria')
        : 'generic'
    );

  return {
    id: apiList.id,
    name: apiList.name || '',
    listType: inferredListType,
    usageContexts: usageContexts.length > 0 ? usageContexts : ['cohort', 'extraction'],
    isPrivate: Boolean(apiList.isPrivate),
    isDefault: Boolean(apiList.isDefault),
    createdBy: apiList.createdBy || '',
    parameterIds: normalizeArray(apiList.parameterIds).map(id => Number(id)),
    additionRequests: normalizeArray(apiList.additionRequests)
      .map(normalizeWorkingListRequest)
      .filter(Boolean),
    createdAt: apiList.createdAt || null,
    updatedAt: apiList.updatedAt || null,
  };
}

/**
 * Der Name, unter dem eine neu angelegte Favoriten-Liste entsteht (#255, Runde
 * 2), und der Fallback-Name, an dem `resolveFavoritesList` sie erkennt, wenn
 * kein `isDefault`-Flag gesetzt ist (das Flag vergibt allein das Backend).
 * Eine Konstante statt zweier Literale, damit Erzeugen und Erkennen nie
 * auseinanderlaufen.
 */
export const FAVORITES_LIST_NAME = 'Favoriten';

/**
 * Die "Favoriten"-Arbeitsliste des angemeldeten Nutzers -- dieselbe Regel, die
 * die Startseiten-Kachel seit #227 verwendet (`isDefault` zuerst, sonst der Name
 * "Favoriten"). Beide Stellen muessen dieselbe Liste finden, sonst zaehlen
 * Kachel und Stern zwei verschiedene Mengen (#255).
 *
 * Nur private Listen zaehlen (#255, Runde 2): das Hinzufuegen zu einer
 * oeffentlichen Liste legt keinen Eintrag an, sondern einen Vorschlag
 * (`request_created`), der erst nach Zustimmung erscheint. Ein Stern auf einer
 * solchen Liste wuerde beim ersten Klick nichts setzen und beim zweiten nichts
 * zu entfernen finden -- also gilt eine oeffentliche Default-/"Favoriten"-Liste
 * hier als nicht vorhanden, nicht anders als gar keine Liste.
 */
export function resolveFavoritesList(lists) {
  const normalizedLists = Array.isArray(lists) ? lists : [];
  const privateLists = normalizedLists.filter(list => list?.isPrivate);
  return (
    privateLists.find(list => list.isDefault) ||
    privateLists.find(list => (list.name || '').toLowerCase() === FAVORITES_LIST_NAME.toLowerCase()) ||
    null
  );
}

function transformDataRequest(apiRequest) {
  if (!apiRequest || typeof apiRequest !== 'object') {
    return null;
  }

  const payload = normalizeObject(apiRequest.requestPayload) || {};
  const selectedParameters = normalizeArray(payload.selectedParameters)
    .map(parameter => {
      if (!parameter || typeof parameter !== 'object') {
        return null;
      }

      return {
        id: parameter.id ?? parameter.parameterId ?? parameter.parameter_id,
        name: parameter.name || parameter.localName || parameter.parameterName || '',
      };
    })
    .filter(Boolean);
  const parameterIds = normalizeArray(apiRequest.parameterIds)
    .map(id => Number(id))
    .filter(Number.isInteger);
  const variableCount =
    selectedParameters.length > 0
      ? selectedParameters.length
      : parseOptionalNumber(payload.selectedParameterCount) ?? parameterIds.length;

  return {
    id: apiRequest.id,
    title: apiRequest.title || '',
    requestType: apiRequest.requestType || 'feasibility',
    status: apiRequest.status || 'pending_approval',
    workingListId: apiRequest.workingListId ?? null,
    workingListName: apiRequest.workingListName || '',
    workingListUsageContexts: normalizeArray(apiRequest.workingListUsageContexts),
    parameterIds,
    cohortName: apiRequest.cohortName || payload.cohortName || '',
    cohortId: apiRequest.cohortReference || payload.cohortId || '',
    requestedBy: apiRequest.requestedBy || '',
    approvedBy: apiRequest.approvedBy || '',
    requestedAt: apiRequest.createdAt || null,
    approvedAt: apiRequest.approvedAt || null,
    completedAt: apiRequest.completedAt || null,
    rejectionReason: apiRequest.rejectionReason || '',
    notes: apiRequest.notes || '',
    estimatedPatients: parseOptionalNumber(payload.estimatedPatients),
    variableCount,
    variables: selectedParameters,
    requestPayload: payload,
  };
}

function normalizeWorkingListSelection(selection) {
  const activeListIds = normalizeArray(selection?.activeListIds)
    .map(id => Number(id))
    .filter(Number.isInteger)
    .slice(0, 2);

  return {
    activeListIds,
    showOnlyActiveListParams: Boolean(selection?.showOnlyActiveListParams && activeListIds.length > 0),
    listComparisonFilter:
      activeListIds.length === 2
        ? (selection?.listComparisonFilter || 'all')
        : 'all',
  };
}

function buildCatalogQueryString(filters = {}) {
  const params = new URLSearchParams();

  const appendValue = (key, value) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    if (Array.isArray(value)) {
      const normalizedValues = value
        .map(item => String(item).trim())
        .filter(Boolean);
      if (normalizedValues.length > 0) {
        params.set(key, normalizedValues.join(','));
      }
      return;
    }

    const normalizedValue = String(value).trim();
    if (normalizedValue) {
      params.set(key, normalizedValue);
    }
  };

  appendValue('q', filters.q);
  appendValue('domain', filters.domain);
  appendValue('sensitivity', filters.sensitivity);
  appendValue('source_system', filters.sourceSystem);
  appendValue('omop_status', filters.omopStatus);
  // Die zweite Darstellung (#770/#772). Der Filtername ist snake_case, die
  // Antwortfelder kommen camelCase zurueck -- ein camelCase-Filter wird vom
  // Backend ausdruecklich mit 400 abgewiesen statt still ignoriert (#247).
  appendValue('openehr_status', filters.openehrStatus);
  appendValue('tags', filters.tags);
  appendValue('stations', filters.stations);
  appendValue('ordering', filters.ordering);
  appendValue('ids', filters.ids);

  return params.toString();
}

function buildCatalogCollectionUrl(endpoint, filters = {}) {
  const queryString = buildCatalogQueryString(filters);
  return queryString ? `${buildCatalogUrl(endpoint)}?${queryString}` : buildCatalogUrl(endpoint);
}

function transformSavedSet(apiSet) {
  if (!apiSet || typeof apiSet !== 'object') {
    return null;
  }

  return {
    id: apiSet.id,
    name: apiSet.name || '',
    type: apiSet.setType || 'parameters',
    description: apiSet.description || '',
    isPublic: Boolean(apiSet.isPublic),
    createdBy: apiSet.createdBy || '',
    items: apiSet.items && typeof apiSet.items === 'object' ? apiSet.items : {},
    icon: apiSet.icon || '📦',
    color: apiSet.color || 'primary',
    createdAt: apiSet.createdAt || null,
    updatedAt: apiSet.updatedAt || null,
  };
}
/**
 * Transform a single API parameter item to the shape the frontend expects.
 */
function transformParameter(apiItem) {
  const displayData =
    normalizeObject(apiItem.displayData);
  const synonyms = normalizeArray(apiItem.synonyms)
    .map(normalizeSynonym)
    .filter(Boolean);
  const approvedSynonyms = synonyms.filter(synonym => synonym.approved !== false);
  const frequencyMetrics = normalizeFrequencyMetrics(apiItem, displayData);
  const caseCoverage = parseOptionalNumber(apiItem.caseCoverage);
  const stationCount = parseOptionalNumber(apiItem.stationCount);
  const omopMapping = normalizeObject(apiItem.omopMapping) || normalizeObject(displayData?.omopMapping);
  // **Der camelCase-Renderer greift bis in die JSON-Nutzlast hinein.** Im
  // Backend heisst das Feld `in_omop_abbildbar`, hier kommt `inOmopAbbildbar`
  // an. Wer den Block mit den Schluesseln aus der Pipeline liest, findet
  // nichts -- und zwar lautlos: `undefined` sieht aus wie "keine Angabe".
  const openehrMapping = normalizeObject(apiItem.openehrMapping);
  // **Nicht jede Frequenzzahl ist ein Detaildatensatz** (#789).
  //
  // Die Katalogliste liefert `caseCoverage` und `stationCount` **flach** je
  // Zeile mit. `normalizeFrequencyMetrics` baut daraus ein belegtes Objekt --
  // und damit galt `hasDetailData` als wahr, worauf `ParameterRow` den
  // Nachladevorgang ausliess (`if (isExpanded && !param.hasDetailData)`). Die
  // aufgeklappte Zeile zeigte deshalb dauerhaft weder Messumfang noch
  // Messmuster noch Histogramm -- **obwohl die Daten da sind**: am 2026-09-18
  // auf hospital1 nachgemessen, 3899 von 3900 Parametern tragen
  // `valueHistogram` und `units` in `display_data`, und die Detailantwort
  // liefert beides.
  //
  // Die Unterscheidung ist die **Quelle**, nicht der Wert: kommen die
  // Kennzahlen aus `statistics` oder `frequencyMetrics`, stammen sie aus einer
  // Detailantwort (so belegt es der Test aus #101, Runde 4, und das bleibt
  // so). Kommen sie nur aus den flachen Altfeldern, ist es eine Listenzeile.
  const frequenzAusDetailquelle = Boolean(
    normalizeObject(apiItem.statistics) || normalizeObject(apiItem.frequencyMetrics)
  );
  const hasStructuredDetailData = Boolean(
    (frequencyMetrics && frequenzAusDetailquelle) ||
    normalizeArray(apiItem.stationCoverage).length > 0 ||
    normalizeObject(apiItem.measurementPattern) ||
    omopMapping
  );

  return {
    id: apiItem.id,
    parameterId: apiItem.parameterId,
    name: apiItem.localName,
    omopName: apiItem.omopName || '',
    description: apiItem.description || '',
    // Das Profiling-Datum als eigenes Feld (#243). Bis heute stand es als Teil
    // von `description` -- einem beim Import auf deutsch eingefrorenen Satz, den
    // keine i18n uebersetzen kann. Hier kommt es als ISO-Datum ohne Uhrzeit; die
    // Schreibweise macht die Oberflaeche, die die Sprache kennt.
    profiledAt: apiItem.profiledAt || displayData?.extractedAt?.slice(0, 10) || '',
    tags: normalizeArray(apiItem.baseTags),
    stations: normalizeArray(apiItem.stations),
    frequency: caseCoverage,
    // Kein Rueckfall auf einen erfundenen Zustand (#248). Liefert die API keinen
    // Status, bleibt das Feld leer -- die Oberflaeche zeigt das als "Unbekannt"
    // (helpers.js:getOMOPStatusLabel), statt einen Zustand vorzutaeuschen, den
    // nichts belegt. `omopMapping?.status` (Review Runde 1) ist bewusst NICHT
    // Teil des Rueckfalls: das ist der Workflow-Status des Mapping-Tools
    // (pending/mapped/unmapped, siehe OmopMappingDetail.js), ein anderer
    // Wertevorrat als der der Katalogkarte (validated/suggested/not_mapped).
    // Wer hier durchreicht, bekommt beim Aufklappen ploetzlich "Ausstehend"
    // angezeigt, obwohl die Liste zuvor etwas anderes zeigte -- derselbe Fehler
    // wie der literale 'pending'-Rueckfall, nur eine Ebene tiefer versteckt.
    omopStatus: apiItem.omopStatus || '',
    omopCode: apiItem.omopCode || omopMapping?.topMatch?.conceptCode || '',
    omopDomain: apiItem.domain || omopMapping?.topMatch?.domain || '',
    omopDescription: displayData?.omopDescription || '',
    omopVocabulary: omopMapping?.topMatch?.vocabulary || '',
    // Die openEHR-Darstellung (#770). Gleiches Vokabular wie `omopStatus`
    // (validated/suggested/not_mapped) -- bewusst kein zweites, sonst waere
    // die Frage "openEHR ja, OMOP nein" nicht in einem Filter formulierbar.
    // Auch hier kein Rueckfall auf einen erfundenen Zustand (#247/#248):
    // schweigt die API, bleibt das Feld leer.
    openehrName: apiItem.openehrName || '',
    openehrCode: apiItem.openehrCode || openehrMapping?.archetyp || '',
    openehrStatus: apiItem.openehrStatus || '',
    openehrMapping,
    openehrAqlPfade: normalizeArray(openehrMapping?.aqlPfade),
    // **Drei Zustaende, nicht zwei.** `true` heisst gemessen und abbildbar,
    // `false` heisst gemessen und nicht abbildbar, `null` heisst nicht
    // gemessen. Ein fehlender Wert darf nicht zu `false` zusammenfallen:
    // das waere die Behauptung einer Messung, die es nicht gibt (siehe die
    // Dreiteilung in argus-olymp-service-openehr-mapping, bundle.py).
    inOmopAbbildbar:
      typeof openehrMapping?.inOmopAbbildbar === 'boolean'
        ? openehrMapping.inOmopAbbildbar
        : null,
    omopFragmentierung: parseOptionalNumber(openehrMapping?.omopFragmentierung),
    sensitivity: apiItem.sensitivity || '',
    sourceSystem: apiItem.sourceSystem || '',
    stationCount,
    synonyms,
    approvedSynonyms,
    availableSince: apiItem.availableSince || apiItem.statistics?.availableSince || null,
    usagePattern: apiItem.usagePattern || apiItem.statistics?.usagePattern || '',
    frequencyMetrics,
    units: mergeUnitsByCasing(displayData?.units),
    // Die Werteverteilung (#215, profile_bundle 1.2). Sie liegt neben `units`,
    // weil sie dieselbe Bauform hat: {value, count, percentage}, absteigend, der
    // Sammeleintrag am Ende. Fehlt sie -- ein Bundle 1.0/1.1 --, bleibt die
    // Liste leer und der aufgeklappte Bereich zeigt statt dessen die
    // Begruendung, sofern eine da ist.
    valueDistribution: normalizeArray(displayData?.valueDistribution),
    valueDistributionReason: displayData?.valueDistributionReason || '',
    // Klasseneinteilung numerischer Werte (#245, profile_bundle 1.3). Ein
    // Parameter traegt entweder Klassen oder eine Werteliste, nie beides --
    // welche, entscheidet der Erzeuger an der Wertspalte. Fehlt das Feld (ein
    // Bundle 1.0 bis 1.2), bleibt die Liste leer und die Karte zeigt den
    // Block nicht.
    valueHistogram: normalizeArray(displayData?.valueHistogram),
    // Der Quellcode fuer die Mapping-Karte (#243). Er stand bis heute nur im
    // Klartext der Listenzeile; dort steht jetzt das Datum, und der Code
    // erscheint als Chip neben Tabellen und Spalten.
    sourceCode: displayData?.sourceCode || '',
    domainGuessReason: displayData?.domainGuessReason || '',
    dataMapping:
      displayData?.dataMapping && typeof displayData.dataMapping === 'object'
        ? displayData.dataMapping
        : null,
    stationDetails: normalizeArray(displayData?.stationDetails),
    tagDetails: normalizeArray(displayData?.tagDetails),
    similarParameters: normalizeArray(displayData?.similarParameters),
    omopMapping,
    // Herkunft einer Zuordnung (#257): importiert / KI-Vorschlag / vom Menschen
    // validiert. `omopMappingSource` traegt den Rohwert aus dem
    // Backend-Enum (data_catalog/models.py: SOURCE_IMPORT="import",
    // SOURCE_AI="ai", SOURCE_USER="user", SOURCE_MANUAL="manual",
    // SOURCE_SYSTEM="system"); ob validiert wurde, steht bereits in
    // `omopStatus`. Fehlt das Feld, bleibt es der leere String -- **kein**
    // Rueckfall auf einen erfundenen Wert (#247/#248).
    omopMappingSource: omopMapping?.source || '',
    // Nur bei importierten Zuordnungen belegt: Sammlung und Lizenz der
    // Quelle (AK2). Auch hier kein erfundener Text, wenn die API dazu
    // schweigt.
    omopMappingSourceCollection: omopMapping?.sourceCollection || '',
    omopMappingSourceLicense: omopMapping?.sourceLicense || '',
    sourceSystemInfo: {
      name: apiItem.sourceSystem || displayData?.dataMapping?.system || '',
      updateFrequency: displayData?.sourceSystem?.updateFrequency || null,
      lastSync: displayData?.sourceSystem?.lastSync || null,
      reliability: displayData?.sourceSystem?.reliability || null,
    },
    engineerNotes: normalizeArray(displayData?.engineerNotes),
    comments: normalizeArray(displayData?.comments || omopMapping?.comments)
      .map(normalizeComment)
      .filter(Boolean),
    displayData: displayData || {},
    hasDetailData: Boolean(displayData) || hasStructuredDetailData,
    // Nur ParameterDetail.js liest dieses Feld noch; die Katalogliste ermittelt
    // `isFavorite` seit #255 aus der Arbeitsliste (`resolveFavoritesList`), weil
    // nur die -- nicht dieses API-Feld -- auch die Startseiten-Kachel speist.
    isFavorite: Boolean(displayData?.isFavorite),
  };
}

// ─── public API ──────────────────────────────────────────────────────────────

/**
 * Fetch all catalog parameters (paginated – fetches all pages).
 * Returns { parameters, source } where source is 'api' or 'error'.
 *
 * `onPage`, wenn angegeben, bekommt nach jeder beantworteten Seite die bis
 * dahin gesammelten (schon umgeformten) Parameter -- der Vollsatz-Modus in
 * DataCatalog.js zeigt damit die erste Seite, sobald sie da ist, statt auf
 * alle ~10 Anfragen zu warten (#191, AK3). Schlaegt eine spaetere Seite fehl,
 * gilt weiterhin die bisherige Regel: der Aufruf insgesamt meldet `source:
 * 'error'` und eine leere Liste, nie eine unvollstaendige als Erfolg
 * getarnt.
 */
export async function getParameters(filters = {}, { onPage } = {}) {
  try {
    let allResults = [];
    let url = buildCatalogCollectionUrl(API_ENDPOINTS.CATALOG.LIST, {
      ...filters,
      pageSize: undefined,
    });
    url = `${url}${url.includes('?') ? '&' : '?'}page_size=200`;
    let pageCount = 0;

    while (url) {
      pageCount += 1;
      if (pageCount > MAX_PAGES) {
        throw new Error(`Catalog pagination exceeded ${MAX_PAGES} pages`);
      }

      const data = await apiFetch(url, { allowAnonymousRetry: true });
      allResults = allResults.concat((data.results || []).map(transformParameter));
      if (onPage) {
        onPage(allResults.slice());
      }
      url = data.next || null;
    }

    return {
      parameters: allResults,
      source: 'api',
    };
  } catch (err) {
    console.warn('[catalogService] getParameters failed:', err.message);
    return { parameters: [], source: 'error', error: err };
  }
}

/**
 * Holt **eine** Seite des Katalogs -- eine Anfrage, nicht alle.
 *
 * Der Unterschied zu `getParameters` ist der ganze Zweck (argus-platform #193):
 * dort laufen fuer 1977 Eintraege zehn Anfragen ueber rund sechs Sekunden, und
 * danach stehen ~4000 Zeilen im DOM. Hier ist es eine Anfrage; die Gesamtzahl kommt
 * aus `count`, damit "n Parameter gefunden" richtig bleibt, ohne dass die Seite
 * alles geladen hat.
 *
 * `ordering` gehoert zwingend dazu: wer seitenweise laedt und lokal sortiert,
 * sortiert nur die sichtbaren 50 (argus-platform #192). Ein unbekanntes Sortierfeld
 * antwortet die API mit 400 -- das ist gewollt und wird hier nicht verschluckt,
 * sondern erreicht als Fehler die Oberflaeche.
 *
 * **`count: null` im Fehlerfall ist Absicht.** Eine leere Liste mit `count: 0` ist
 * von "keine Treffer" nicht zu unterscheiden, und genau daran ist am 2026-08-24
 * eine Fehlersuche vorbeigelaufen: die Seite sah aus wie ein leerer Standort,
 * waehrend im Proxy-Log erfolgreiche Abrufe standen (#191).
 *
 * @returns {Promise<{parameters: Array, count: number|null, page: number,
 *   pageSize: number, hasNext: boolean, source: 'api'|'error', error?: Error}>}
 */
export async function getParameterPage({
  filters = {},
  page = 1,
  pageSize = 50,
  ordering = '',
} = {}) {
  const angefragteSeite = Math.max(1, Number(page) || 1);

  try {
    const params = new URLSearchParams(buildCatalogQueryString({ ...filters, ordering }));
    params.set('page', String(angefragteSeite));
    params.set('page_size', String(pageSize));

    const data = await apiFetch(`${buildCatalogUrl(API_ENDPOINTS.CATALOG.LIST)}?${params.toString()}`, {
      allowAnonymousRetry: true,
    });

    // Die Liste kann seitenweise (`{count, next, results}`) kommen; alles andere
    // ist ein Befund und keine leere Seite.
    if (!Array.isArray(data?.results)) {
      throw new Error('Catalog response has no results array');
    }

    return {
      parameters: data.results.map(transformParameter),
      count: typeof data.count === 'number' ? data.count : data.results.length,
      page: angefragteSeite,
      pageSize,
      hasNext: Boolean(data.next),
      source: 'api',
    };
  } catch (err) {
    console.warn('[catalogService] getParameterPage failed:', err.message);
    return {
      parameters: [],
      count: null,
      page: angefragteSeite,
      pageSize,
      hasNext: false,
      source: 'error',
      error: err,
    };
  }
}

/**
 * Normalize a single OMOP concept candidate from GET /api/mapping/candidates.
 * The endpoint's field naming (snake_case) is not exercised by any fixture in
 * this repo -- the backend lives in a separate service -- so both snake_case
 * and camelCase are accepted defensively. Fields the caller reads through its
 * own accessors (origin, concept_class, standard_concept, ...) are passed
 * through untouched; only the fields read directly (conceptName, conceptId, ...)
 * are normalized here.
 */
export function transformMappingCandidate(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  return {
    ...raw,
    conceptId: raw.conceptId ?? raw.concept_id ?? raw.id ?? '',
    conceptCode: raw.conceptCode ?? raw.concept_code ?? raw.code ?? '',
    conceptName: raw.conceptName ?? raw.concept_name ?? raw.name ?? '',
    vocabulary: raw.vocabulary ?? raw.vocabulary_id ?? '',
    domain: raw.domain ?? raw.domain_id ?? '',
    score: raw.score ?? raw.matchScore ?? raw.confidenceScore ?? null,
  };
}

/**
 * OMOP concept candidates for a parameter (#98). Goes through the same
 * apiFetch as the rest of this service, so auth headers, the 10s timeout and
 * 401 handling apply here too -- a bare `fetch` would silently 401 in any
 * authenticated environment.
 */
export async function getMappingCandidates(parameterId) {
  try {
    const params = new URLSearchParams({ parameter_id: String(parameterId) });
    const data = await apiFetch(
      `${buildCatalogUrl(API_ENDPOINTS.MAPPING.CANDIDATES)}?${params.toString()}`,
      { allowAnonymousRetry: true }
    );
    const list = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
    return { candidates: list.map(transformMappingCandidate), source: 'api' };
  } catch (err) {
    console.warn('[catalogService] getMappingCandidates failed:', err.message);
    return { candidates: [], source: 'error', error: err };
  }
}

/**
 * Holt genau die Parameter mit den angegebenen IDs -- ohne den vollen
 * Seitenweg ueber alle ~1977 Eintraege (argus-platform #277). Ersetzt fuer
 * KI-Vorschlaege, ein geladenes Set und "nur Parameter der aktiven Liste" den
 * bisherigen Weg ueber alle Seiten (#193); siehe `idsFuerFilter` in
 * `katalogBlaetterung.js`.
 *
 * `page_size` bekommt hier die Anzahl der angefragten IDs, damit im
 * Regelfall eine Anfrage reicht. **Garantiert ist das nicht** (Runde 1,
 * Review-Blocker B1): kappt das Backend `page_size` auf einen eigenen
 * Hoechstwert, kommt `count` voll und `results` gekuerzt zurueck, mit
 * gesetztem `next` -- wie bei jeder anderen paginierten Antwort auch. Diese
 * Funktion folgt `next` deshalb genauso wie `getParameters`, statt sich auf
 * eine einzige Antwort zu verlassen.
 *
 * Ruft diese Funktion **nie** mit einem leeren `ids`-Array auf: die API sieht
 * einen fehlenden `ids`-Parameter nicht anders als einen leeren und liefert
 * dann den ganzen Katalog zurueck. Das Abfangen des leeren Falls ist Sache
 * der aufrufenden Seite (DataCatalog.js).
 */
export async function getParametersByIds(ids, filters = {}) {
  try {
    const params = new URLSearchParams(buildCatalogQueryString({ ...filters, ids }));
    params.set('page_size', String(ids.length));

    let url = `${buildCatalogUrl(API_ENDPOINTS.CATALOG.LIST)}?${params.toString()}`;
    let allResults = [];
    let count = null;
    let pageCount = 0;

    while (url) {
      pageCount += 1;
      if (pageCount > MAX_PAGES) {
        throw new Error(`Catalog id lookup exceeded ${MAX_PAGES} pages`);
      }

      const data = await apiFetch(url, { allowAnonymousRetry: true });

      if (!Array.isArray(data?.results)) {
        throw new Error('Catalog response has no results array');
      }

      allResults = allResults.concat(data.results.map(transformParameter));
      count = typeof data.count === 'number' ? data.count : allResults.length;
      url = data.next || null;
    }

    return {
      parameters: allResults,
      count,
      source: 'api',
    };
  } catch (err) {
    console.warn('[catalogService] getParametersByIds failed:', err.message);
    return { parameters: [], count: null, source: 'error', error: err };
  }
}

/**
 * Fetch a single parameter by database id or stable parameter identifier.
 */
export async function getParameter(id) {
  try {
    const data = await apiFetch(
      buildCatalogUrl(API_ENDPOINTS.CATALOG.DETAIL.replace('{id}', getParameterLookupId(id))),
      { allowAnonymousRetry: true }
    );
    return { parameter: transformParameter(data), source: 'api' };
  } catch (err) {
    console.warn('[catalogService] getParameter failed:', err.message);
    return { parameter: null, source: 'error', error: err };
  }
}

/**
 * Free-text + filter search.
 * Returns { parameters, source }.
 */
export async function searchParameters({ q = '', domain, sensitivity, sourceSystem, omopStatus, tags, stations } = {}) {
  try {
    const data = await apiFetch(
      buildCatalogCollectionUrl(API_ENDPOINTS.CATALOG.SEARCH, {
        q,
        domain,
        sensitivity,
        sourceSystem,
        omopStatus,
        tags,
        stations,
      }),
      { allowAnonymousRetry: true }
    );
    return {
      parameters: (data.results || []).map(transformParameter),
      source: 'api',
    };
  } catch (err) {
    console.warn('[catalogService] searchParameters failed:', err.message);
    return { parameters: [], source: 'error', error: err };
  }
}

/**
 * Aggregate stats: totalParameters, mappedCount, validatedCount, pendingCount,
 * domainDistribution, sensitivityDistribution.
 */
export async function getCatalogStats() {
  try {
    const data = await apiFetch(buildCatalogUrl(API_ENDPOINTS.CATALOG.STATS), { allowAnonymousRetry: true });
    return { stats: data, source: 'api' };
  } catch (err) {
    console.warn('[catalogService] getCatalogStats failed:', err.message);
    return { stats: null, source: 'error', error: err };
  }
}

/**
 * Distinct tags with usage counts.
 * Returns { tags: [{ value, count }], source }.
 */
export async function getCatalogTags(filters = {}) {
  try {
    const data = await apiFetch(buildCatalogCollectionUrl(API_ENDPOINTS.CATALOG.TAGS, filters), {
      allowAnonymousRetry: true,
    });
    return { tags: data.results || [], source: 'api' };
  } catch (err) {
    console.warn('[catalogService] getCatalogTags failed:', err.message);
    return { tags: [], source: 'error', error: err };
  }
}

/**
 * Distinct stations with usage counts.
 * Returns { stations: [{ value, count }], source }.
 */
export async function getCatalogStations(filters = {}) {
  try {
    const data = await apiFetch(buildCatalogCollectionUrl(API_ENDPOINTS.CATALOG.STATIONS, filters), {
      allowAnonymousRetry: true,
    });
    return { stations: data.results || [], source: 'api' };
  } catch (err) {
    console.warn('[catalogService] getCatalogStations failed:', err.message);
    return { stations: [], source: 'error', error: err };
  }
}

/**
 * Distinct source systems with usage counts.
 * Returns { sourceSystems: [{ value, count }], source }.
 *
 * Warum als eigener Abruf: die Auswahl dieses Filters kam bisher aus den geladenen
 * Zeilen (`[...new Set(catalogItems.map(p => p.sourceSystem))]`). Das ging, solange
 * die Seite alle 1977 hielt -- mit Blaetterung waere daraus die Auswahl der ersten
 * Seite geworden, und ein Filterwert, den es gibt, waere unsichtbar verschwunden.
 */
export async function getCatalogSourceSystems(filters = {}) {
  try {
    const data = await apiFetch(buildCatalogCollectionUrl(API_ENDPOINTS.CATALOG.SOURCE_SYSTEMS, filters), {
      allowAnonymousRetry: true,
    });
    return { sourceSystems: data.results || [], source: 'api' };
  } catch (err) {
    console.warn('[catalogService] getCatalogSourceSystems failed:', err.message);
    return { sourceSystems: [], source: 'error', error: err };
  }
}

/**
 * openEHR-Archetypen mit Anzahl fuer die Chip-Zeile (#788).
 *
 * **Anders als die Nachbarn: kein eigener Endpunkt.** `getCatalogTags` und
 * Co. haben je einen; fuer die Archetypen gibt es keinen -- der geratene Pfad
 * `/olymp/catalog/openehr-archetypes/` antwortete gegen hospital1 mit 404.
 * Die Verteilung liefert `stats/` als `openehrArchetypeDistribution`, und
 * zwar als **Objekt** Archetyp -> Anzahl, nicht als `results`-Array. Hier
 * wird sie in die Form `[{ value, count }]` gebracht, die `ArchetypChips`
 * erwartet.
 *
 * Der Bucket ohne Zuordnung heisst serverseitig `"keiner"`
 * (`ARCHETYP_OHNE_ZUORDNUNG` in `data_catalog/views.py`), nicht `""` -- und
 * genau dieser Wert geht auch wieder als Filter an die API zurueck.
 *
 * Returns { archetypes: [{ value, count }], source }.
 */
export async function getCatalogArchetypes(filters = {}) {
  try {
    const data = await apiFetch(buildCatalogCollectionUrl(API_ENDPOINTS.CATALOG.STATS, filters), {
      allowAnonymousRetry: true,
    });
    const verteilung = data?.openehrArchetypeDistribution;
    if (!verteilung || typeof verteilung !== 'object') {
      // Kein Feld heisst "diese Instanz kennt die Verteilung nicht" -- nicht
      // "es gibt keine Archetypen". Die Unterscheidung traegt `source`.
      return { archetypes: [], source: 'unsupported' };
    }
    const archetypes = Object.entries(verteilung)
      .map(([value, count]) => ({ value, count: Number(count) || 0 }))
      .sort((a, b) => {
        // Der Bucket ohne Zuordnung ans Ende -- er ist auf hospital1 der
        // groesste (2.377 von 3.900) und wuerde die Zeile sonst anfuehren,
        // obwohl er das am wenigsten Aussagekraeftige ist.
        if (a.value === ARCHETYP_OHNE_ZUORDNUNG) return 1;
        if (b.value === ARCHETYP_OHNE_ZUORDNUNG) return -1;
        return b.count - a.count || a.value.localeCompare(b.value);
      });
    return { archetypes, source: 'api' };
  } catch (err) {
    console.warn('[catalogService] getCatalogArchetypes failed:', err.message);
    return { archetypes: [], source: 'error', error: err };
  }
}

/**
 * Anzahl je Statuswert fuer die Filterleiste (#782 AK3c), aus derselben
 * `stats/`-Antwort wie `getCatalogArchetypes`.
 *
 * Die Aufschluesselung fehlt auf hospital1 heute noch -- sie kommt aus #783.
 * Wie dort: ein fehlendes Feld heisst "diese Instanz kennt die
 * Aufschluesselung nicht", nicht "es gibt keine Treffer". Beide Achsen
 * degradieren unabhaengig voneinander auf `null`, falls #783 sie nacheinander
 * ausliefert statt zusammen.
 *
 * Returns { omopStatusCounts: Map|null, openehrStatusCounts: Map|null, source }.
 */
export async function getCatalogStatusCounts(filters = {}) {
  try {
    const data = await apiFetch(buildCatalogCollectionUrl(API_ENDPOINTS.CATALOG.STATS, filters), {
      allowAnonymousRetry: true,
    });
    const alsMap = (verteilung) => (verteilung && typeof verteilung === 'object'
      ? new Map(Object.entries(verteilung).map(([value, count]) => [value, Number(count) || 0]))
      : null);
    return {
      omopStatusCounts: alsMap(data?.omopStatusDistribution),
      openehrStatusCounts: alsMap(data?.openehrStatusDistribution),
      source: 'api',
    };
  } catch (err) {
    console.warn('[catalogService] getCatalogStatusCounts failed:', err.message);
    return { omopStatusCounts: null, openehrStatusCounts: null, source: 'error', error: err };
  }
}

/**
 * Simple connectivity check — resolves true if the API is reachable.
 */
export async function isCatalogApiReachable() {
  try {
    await apiFetch(buildCatalogUrl(API_ENDPOINTS.CATALOG.STATS), { allowAnonymousRetry: true });
    return true;
  } catch {
    return false;
  }
}

export async function getWorkingLists() {
  try {
    const data = await apiFetch(buildListUrl());
    return {
      lists: normalizeArray(data).map(transformWorkingList).filter(Boolean),
      source: 'api',
    };
  } catch (err) {
    console.warn('[catalogService] getWorkingLists failed:', err.message);
    return { lists: [], source: 'error', error: err };
  }
}

export async function getSavedSets() {
  try {
    const data = await apiFetch(buildSetUrl());
    return {
      sets: normalizeArray(data).map(transformSavedSet).filter(Boolean),
      source: 'api',
    };
  } catch (err) {
    console.warn('[catalogService] getSavedSets failed:', err.message);
    return { sets: [], source: 'error', error: err };
  }
}

export async function getWorkingListSelection() {
  try {
    const data = await apiFetch(buildCatalogUrl(API_ENDPOINTS.LISTS.SELECTION));
    return {
      selection: normalizeWorkingListSelection(data),
      source: 'api',
    };
  } catch (err) {
    console.warn('[catalogService] getWorkingListSelection failed:', err.message);
    return {
      selection: normalizeWorkingListSelection(null),
      source: 'error',
      error: err,
    };
  }
}

export async function updateWorkingListSelection(selection) {
  const data = await apiFetch(buildCatalogUrl(API_ENDPOINTS.LISTS.SELECTION), {
    method: 'PATCH',
    body: selection,
  });
  return normalizeWorkingListSelection(data);
}
export async function createWorkingList({
  name,
  listType = 'generic',
  usageContexts,
  isPrivate = true,
}) {
  const data = await apiFetch(buildListUrl(), {
    method: 'POST',
    body: {
      name,
      listType,
      usageContexts: usageContexts || (
        listType === 'criteria'
          ? ['cohort']
          : listType === 'extraction'
            ? ['extraction']
            : ['cohort', 'extraction']
      ),
      isPrivate,
    },
  });
  return transformWorkingList(data);
}

export async function updateWorkingList(id, updates) {
  const nextUpdates = { ...updates };
  if (nextUpdates.listType && !nextUpdates.usageContexts) {
    nextUpdates.usageContexts =
      nextUpdates.listType === 'criteria'
        ? ['cohort']
        : nextUpdates.listType === 'extraction'
          ? ['extraction']
          : ['cohort', 'extraction'];
  }
  const data = await apiFetch(buildListUrl(`${id}`), {
    method: 'PATCH',
    body: nextUpdates,
  });
  return transformWorkingList(data);
}

export async function deleteWorkingList(id) {
  await apiFetch(buildListUrl(`${id}`), {
    method: 'DELETE',
  });
}

export async function duplicateWorkingList(id) {
  const data = await apiFetch(buildListUrl(`${id}/duplicate`), {
    method: 'POST',
  });
  return transformWorkingList(data);
}

/**
 * Kohorten des angemeldeten Nutzers.
 *
 * Zaehlt fuer die Dashboard-Kachel. Bewusst ohne Umformung: gebraucht wird
 * bisher nur die Anzahl, und eine Umformung, die niemand liest, veraltet still.
 * Der Fehlerfall gibt eine leere Liste **und** `source: 'error'` zurueck -- die
 * Oberflaeche muss "keine Kohorten" von "nicht erreichbar" unterscheiden
 * koennen. Genau das ging bisher nicht (#227).
 */
export async function getCohorts() {
  try {
    const data = await apiFetch(buildCatalogUrl(API_ENDPOINTS.COHORTS.LIST));
    return { cohorts: normalizeArray(data), source: 'api' };
  } catch (err) {
    console.warn('[catalogService] getCohorts failed:', err.message);
    return { cohorts: [], source: 'error', error: err };
  }
}

export async function getDataRequests() {
  try {
    const data = await apiFetch(buildRequestUrl());
    return {
      requests: normalizeArray(data).map(transformDataRequest).filter(Boolean),
      source: 'api',
    };
  } catch (err) {
    console.warn('[catalogService] getDataRequests failed:', err.message);
    return { requests: [], source: 'error', error: err };
  }
}

export async function createDataRequest({
  title,
  requestType,
  workingListId,
  cohortName,
  cohortReference,
  notes,
  requestPayload,
}) {
  const data = await apiFetch(buildRequestUrl(), {
    method: 'POST',
    body: {
      title,
      requestType,
      workingList: workingListId,
      cohortName,
      cohortReference,
      notes,
      requestPayload,
    },
  });
  return transformDataRequest(data);
}

export async function approveDataRequest(id) {
  const data = await apiFetch(buildRequestUrl(`${id}/approve`), {
    method: 'POST',
    body: {},
  });
  return transformDataRequest(data);
}

export async function completeDataRequest(id) {
  const data = await apiFetch(buildRequestUrl(`${id}/complete`), {
    method: 'POST',
    body: {},
  });
  return transformDataRequest(data);
}

export async function rejectDataRequest(id, reason = '') {
  const data = await apiFetch(buildRequestUrl(`${id}/reject`), {
    method: 'POST',
    body: { reason },
  });
  return transformDataRequest(data);
}

export async function addParameterToWorkingList(listId, parameterId) {
  const data = await apiFetch(buildListUrl(`${listId}/parameters`), {
    method: 'POST',
    body: { parameterId },
  });

  return {
    action: data?.action || 'unknown',
    message: data?.message || '',
    list: transformWorkingList(data?.list),
  };
}

export async function removeParameterFromWorkingList(listId, parameterId) {
  const data = await apiFetch(buildListUrl(`${listId}/parameters/${parameterId}`), {
    method: 'DELETE',
  });

  return {
    action: data?.action || 'unknown',
    message: data?.message || '',
    list: transformWorkingList(data?.list),
  };
}

export async function createManualMapping(parameterId, concept) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, 'mapping/manual'), {
    method: 'POST',
    body: {
      conceptId: concept.conceptId,
      conceptCode: concept.conceptCode || '',
      conceptName: concept.conceptName,
      vocabulary: concept.vocabulary || '',
      domain: concept.domain || '',
      confidenceScore: concept.confidenceScore,
      justification: concept.justification || '',
      source: 'manual',
    },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function validateCurrentMapping(parameterId) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, 'mapping/current/validate'), {
    method: 'POST',
    body: {},
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function rejectCurrentMapping(parameterId, reason = '') {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, 'mapping/current/reject'), {
    method: 'POST',
    body: { reason },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

/**
 * Das zweite Gate: der Mensch entscheidet ueber einen Mapping-Vorschlag (#99).
 * `decision` ist `accepted`, `rejected` oder `deferred`; `note` ist bei allen
 * drei optional -- eine Ablehnung verlangt keine Begruendung, bietet sie aber an.
 */
export async function submitMappingDecision(parameterId, decision, note = '') {
  const data = await apiFetch(buildCatalogUrl(API_ENDPOINTS.MAPPING.DECISIONS), {
    method: 'POST',
    body: { parameterId, decision, note },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function commentCurrentMapping(parameterId, text) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, 'mapping/current/comment'), {
    method: 'POST',
    body: { text },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function voteCurrentMapping(parameterId, vote) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, 'mapping/current/vote'), {
    method: 'POST',
    body: { vote },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function createMappingProposal(parameterId, concept) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, 'mapping/proposals'), {
    method: 'POST',
    body: {
      conceptId: concept.conceptId,
      conceptCode: concept.conceptCode || '',
      conceptName: concept.conceptName,
      vocabulary: concept.vocabulary || '',
      domain: concept.domain || '',
      confidenceScore: concept.confidenceScore,
      justification: concept.justification || '',
      metadata: concept.metadata || {},
      source: concept.source || 'user',
    },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function approveMappingProposal(parameterId, proposalId) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `mapping/proposals/${proposalId}/approve`), {
    method: 'POST',
    body: {},
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function rejectMappingProposal(parameterId, proposalId, reason = '') {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `mapping/proposals/${proposalId}/reject`), {
    method: 'POST',
    body: { reason },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function voteMappingProposal(parameterId, proposalId, vote) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `mapping/proposals/${proposalId}/vote`), {
    method: 'POST',
    body: { vote },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function commentMappingProposal(parameterId, proposalId, text) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `mapping/proposals/${proposalId}/comment`), {
    method: 'POST',
    body: { text },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function createCatalogSynonym(parameterId, synonym) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, 'synonyms'), {
    method: 'POST',
    body: {
      term: synonym.term,
      language: synonym.language || 'und',
      context: synonym.context || '',
      source: synonym.source || 'user',
    },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function approveCatalogSynonym(parameterId, synonymId) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `synonyms/${synonymId}/approve`), {
    method: 'POST',
    body: {},
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function rejectCatalogSynonym(parameterId, synonymId, reason = '') {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `synonyms/${synonymId}/reject`), {
    method: 'POST',
    body: { reason },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function voteCatalogSynonym(parameterId, synonymId, vote) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `synonyms/${synonymId}/vote`), {
    method: 'POST',
    body: { vote },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function commentCatalogSynonym(parameterId, synonymId, text) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `synonyms/${synonymId}/comment`), {
    method: 'POST',
    body: { text },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function deleteCatalogSynonym(parameterId, synonymId) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `synonyms/${synonymId}`), {
    method: 'DELETE',
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function createCatalogTag(parameterId, tag) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, 'tags'), {
    method: 'POST',
    body: {
      name: tag.name,
      icon: tag.icon || '',
      isPrivate: Boolean(tag.isPrivate),
      source: tag.source || 'user',
    },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function approveCatalogTag(parameterId, tagId) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `tags/${tagId}/approve`), {
    method: 'POST',
    body: {},
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function rejectCatalogTag(parameterId, tagId, reason = '') {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `tags/${tagId}/reject`), {
    method: 'POST',
    body: { reason },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function voteCatalogTag(parameterId, tagId, vote) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `tags/${tagId}/vote`), {
    method: 'POST',
    body: { vote },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function commentCatalogTag(parameterId, tagId, text) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `tags/${tagId}/comment`), {
    method: 'POST',
    body: { text },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function deleteCatalogTag(parameterId, tagId) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `tags/${tagId}`), {
    method: 'DELETE',
  });

  return { parameter: transformParameter(data), source: 'api' };
}
